/**
 * Task #3263 — Core riusabile dell'ApiClient tenant (estratto da
 * `packages/frontend/src/lib/api.ts` per condividerlo con i FE verticali,
 * a partire da `@easyflow/frontend-w3`).
 *
 * Qui vivono: la whitelist `TENANT_AGNOSTIC_PREFIXES` (SoT unica), la logica
 * `shouldPrefixTenant` (prefix slug su confine di path) e una classe
 * `ApiClientCore` generica con request/get/post/put/patch/delete tipizzati,
 * gestione Bearer token da storage key parametrica, retry singolo post
 * refresh-token e dispatch `auth:unauthorized`.
 *
 * Il frontend Easyflow continua a usare la propria classe `ApiClient`
 * (metodi domain-specific) ma importa da qui whitelist e prefix logic,
 * così i due verticali non divergono mai sul routing tenant.
 */
import { decodeJwtPayload } from './jwt';

export interface CoreApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CoreApiError extends Error {
  status?: number;
  data?: unknown;
  requestId?: string;
}

export function isCoreApiError(err: unknown): err is CoreApiError {
  return err instanceof Error && 'status' in err;
}

/**
 * Prefissi API tenant-agnostici: endpoint montati top-level (`/api/<segment>`)
 * dove il tenant è risolto dal JWT, NON dal path. L'apiClient NON antepone lo
 * slug a questi. SoT unica cross-verticale — spostata qui dal frontend
 * Easyflow (Task #3263), i commenti storici per-voce restano nel changelog.
 */
export const TENANT_AGNOSTIC_PREFIXES: readonly string[] = [
  '/auth',
  // Task #3220 — Agente Profilazione Clienti (tenant via JWT).
  '/profiling-agent',
  '/health',
  '/admin',
  '/master',
  '/superadmin',
  '/system-logs',
  '/messaging',
  '/company-wiki',
  '/storage',
  // Task #1801 — Sedi v2: cataloghi tenant da JWT.
  '/retail-brands',
  '/commercial-areas',
  '/w3-integration',
  '/freeswitch-deploy',
  '/xml-cdr',
  '/openai',
  // Task #1976 — Custom AI: rotte top-level sotto protectedRouter (tenant da JWT).
  '/custom-ai',
  // Reparti HR (lookup destinatari feed/composer): mount top-level su
  // protectedRouter, tenant da JWT; senza twin slug (non in T7_MIGRATED).
  '/departments',
  // Stato FreeSWITCH per la sidebar (`/api/freeswitch/status`): rotta
  // top-level con JWT, senza twin slug (`/freeswitch-deploy` è a parte).
  '/freeswitch',
  // Task #3405 — Incentivazione (commissioning): mount top-level /api/incentivazione, tenant da JWT.
  '/incentivazione',
  // Task #2892 — Meta Business connection (tenant da JWT, mount top-level /api/meta).
  '/meta',
  // Catalogo Natura IVA: dato di riferimento globale read-only (tenant da JWT).
  '/fiscal',
  '/widgets',
  '/public',
  '/video',
  '/v2',
  '/bff',
  '/dialplan',
  '/coaching',
  '/kb',
  '/recordings',
  '/calendar/public',
  // Task #2656 — bootstrap consolidato del Calendar Hub (tenant da JWT, mount /api/calendar).
  '/calendar/bootstrap',
  '/crm/public',
  '/my-modules',
  '/roles/my-permissions',
  '/switchboard/supervisor',
  '/workflow-nodes',
  // Task #1408 — RAG multimodal query (tenant da JWT, top-level mount).
  '/rag',
  // Task #1380 — vista Inventario tenant-from-JWT (no tenantSlug nel path).
  '/inventory',
  // Task #1400 — PA workspace (artifacts/state/approvals/memory) tenant-from-JWT.
  '/personal-assistant/workspace',
  // Task #1417 — Telegram integration (tenant da JWT, top-level mount).
  '/integrations/telegram',
  // Task #1418 — Telegram pairing per notifiche (tenant da JWT, top-level mount).
  '/notifications/telegram',
  // Task #1435 — usage-limits (tenant da JWT, mount sotto protectedRouter).
  '/tenant/usage-limits',
  // Task #1466 — vista unificata Documenti Fiscali (tenant da JWT).
  '/fiscal-documents',
  // Task #1480 — Goods Receipt Drafts (tenant da JWT, mount sotto protectedRouter).
  '/pos/goods-receipt-drafts',
  // Task #1511 — Listini POS (router dedicato `/api/pricelists`, tenant da JWT).
  '/pricelists',
  // Task #1515 — KB metadata schema admin (tenant da JWT, mount sotto protectedRouter).
  '/kb-metadata-schema',
  // Task #1555 — Fiscal Driver Extensions tenant-from-JWT.
  '/fiscal-printers',
  // Task #1660 — Messenger v2 (tenant da JWT).
  '/messenger',
  // Task #1662 — Feed v2 (tenant da JWT).
  '/messenger/feed',
  // Task #1664 — Cross-modulo bridge (tenant da JWT).
  '/cross',
  // Task #1713 — Vista documenti unificata scheda cliente (tenant da JWT).
  '/crm/persons',
  // Task #1746 (T7) Batch A — rotte admin tenant migrate a protectedRouter.
  '/dashboard',
  '/stores',
  '/users',
  '/analytics',
  '/api-keys',
  '/roles',
  '/logs',
  '/audit-log',
  '/support',
  '/tenant',
  // Task #1821 — Dedupe holiday-calendars: rotta canonica protectedRouter.
  '/holiday-calendars',
  // Task #1829 — MCP Catalogo Evergreen (tenant da JWT).
  '/mcp',
  // A2A Gateway (tenant da JWT).
  '/a2a',
  // Architect (orchestrator A2A, tenant da JWT).
  '/architect',
  // Task #2504 — RAG Analytics (tenant da JWT).
  '/rag-analytics',
  // Task #2566 — Entità Legali hub + Enti Finanzianti (tenant da JWT).
  '/legal-entities',
  '/financing-partners',
  // Task #2720 — Recinto (action scope, tenant da JWT).
  '/user-scopes',
  // Task #4257 — Concessioni permesso × portata (tenant da JWT).
  '/permission-grants',
  // Task #2971 — Abbina lead a vendita (tenant da JWT).
  '/crm/lead-sale-match',
  '/crm/person-profile',
  '/crm/journeys',
  // Task #3588 — Consolidamento namespace: tutte le famiglie tenant con twin
  // canonico top-level (tenant da JWT). Il namespace legacy /:tenantSlug/*
  // resta raggiungibile solo via rewrite T7 (deprecato, rimozione nel task
  // dedicato). Alcune voci storiche più specifiche sopra (es. /crm/public)
  // diventano ridondanti ma restano innocue.
  '/ai',
  '/ai-health-check',
  '/ai-quota',
  '/ai-tools',
  '/apollo',
  '/audio',
  '/audio-templates',
  '/calendar',
  '/callbacks',
  '/calls',
  '/cdr',
  '/chatbot',
  '/conference-rooms',
  '/conservazione',
  '/crm',
  '/dialer',
  '/dialplan-debug',
  '/dnc',
  '/elevenlabs',
  '/email',
  '/email-accounts',
  '/extension-groups',
  '/extensions',
  '/files',
  '/gemini-live',
  '/gemini-voice',
  '/geocode',
  '/google-drive',
  '/hr',
  '/inbound-routes',
  '/integrations',
  '/invoices',
  '/ivr',
  '/ivr-menus',
  '/knowledge-base',
  '/landing-pages',
  '/mobile',
  '/my-avatar',
  '/my-extension',
  '/my-profile',
  '/notifications',
  '/opensips-routes',
  '/outbound-routes',
  // Task #3038 — Routine personali del PA (tenant da JWT, owner-scoped).
  '/pa-routines',
  '/personal-assistant',
  '/pos',
  '/queue-monitor',
  '/queues',
  '/registry',
  '/ring-groups',
  '/routing',
  '/security',
  '/sip-extensions',
  '/sip-public-config',
  '/sip-trunks',
  '/system',
  '/tasks',
  '/tenant',
  '/totp',
  '/voicemail-boxes',
  '/voicemail-greetings',
  '/voicemail-messages',
  '/voip',
  '/wakeup-calls',
  // Task #3618 — gestione webhook in uscita (tenant da JWT, mount canonico
  // /api/webhook-configs; consumer: pagina Impostazioni Sviluppatore).
  '/webhook-configs',
  '/workflow-runs',
  '/workflows',
  '/workgroups',
];

export function shouldPrefixTenant(endpoint: string, tenantSlug: string | null): boolean {
  if (!tenantSlug) return false;
  if (endpoint.includes(`/${tenantSlug}/`)) return false;
  // Match prefix solo su confine di path (`/foo` matcha `/foo`, `/foo/x`, `/foo?q=`),
  // NON su parole con stesso prefisso (`/dialplan` NON deve matchare `/dialplan-debug`).
  return !TENANT_AGNOSTIC_PREFIXES.some((p) => {
    if (!endpoint.startsWith(p)) return false;
    const nextChar = endpoint.charAt(p.length);
    return nextChar === '' || nextChar === '/' || nextChar === '?';
  });
}

export interface ApiClientCoreConfig {
  /** Base URL delle API, es. `/api`. */
  baseUrl: string;
  /** Storage key del JWT tenant (es. `edgvoip_tenant_token`). */
  tokenStorageKey: string;
  /** Storage key del refresh token (opzionale: senza, nessun retry-refresh). */
  refreshTokenStorageKey?: string;
  /** Handler refresh: ritorna il nuovo access token o null. */
  refreshHandler?: () => Promise<string | null>;
  /** Callback su 401 definitivo (default: dispatch `auth:unauthorized`). */
  onUnauthorized?: () => void;
}

function readSlugClaim(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Client HTTP generico tenant-aware. I verticali lo istanziano direttamente;
 * il frontend Easyflow mantiene la propria classe domain-specific ma condivide
 * whitelist + prefix logic esportate sopra.
 */
export class ApiClientCore {
  private token: string | null = null;
  private readonly config: ApiClientCoreConfig;

  constructor(config: ApiClientCoreConfig) {
    this.config = config;
  }

  setToken(token: string): void {
    this.token = token;
  }

  clearToken(): void {
    this.token = null;
  }

  getAuthToken(): string {
    return this.token || localStorage.getItem(this.config.tokenStorageKey) || '';
  }

  getTenantSlugFromToken(): string | null {
    const token = this.getAuthToken();
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    if (!payload) return null;
    return readSlugClaim(payload, 'tenantSlug') ?? readSlugClaim(payload, 'tenant_slug');
  }

  buildUrl(endpoint: string): string {
    const tenantSlug = this.getTenantSlugFromToken();
    if (shouldPrefixTenant(endpoint, tenantSlug)) {
      return `${this.config.baseUrl}/${tenantSlug}${endpoint}`;
    }
    return `${this.config.baseUrl}${endpoint}`;
  }

  async get<T = unknown>(endpoint: string): Promise<CoreApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = unknown>(endpoint: string, data?: unknown): Promise<CoreApiResponse<T>> {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'POST',
      body: isFormData ? data : (data !== undefined ? JSON.stringify(data) : undefined),
    });
  }

  async put<T = unknown>(endpoint: string, data?: unknown): Promise<CoreApiResponse<T>> {
    this.assertHasId('PUT', endpoint);
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: isFormData ? data : (data !== undefined ? JSON.stringify(data) : undefined),
    });
  }

  async patch<T = unknown>(endpoint: string, data?: unknown): Promise<CoreApiResponse<T>> {
    this.assertHasId('PATCH', endpoint);
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: isFormData ? data : (data !== undefined ? JSON.stringify(data) : undefined),
    });
  }

  async delete<T = unknown>(endpoint: string): Promise<CoreApiResponse<T>> {
    this.assertHasId('DELETE', endpoint);
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Id-guard runtime per mutazioni (pattern Task #1791): URL costruiti con id
   * `undefined`/`null` falliscono subito con errore esplicito.
   */
  private assertHasId(method: string, endpoint: string): void {
    const path = endpoint.split('?')[0] ?? endpoint;
    const lastSeg = path.split('/').filter(Boolean).pop() ?? '';
    if (lastSeg === 'undefined' || lastSeg === 'null' || path.endsWith('/')) {
      const err: CoreApiError = new Error(
        `${method} ${endpoint}: id assente. Verifica lo stato del componente prima di invocare la mutazione.`
      );
      err.status = 400;
      throw err;
    }
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
    _retryAfterRefresh = false
  ): Promise<CoreApiResponse<T>> {
    const url = this.buildUrl(endpoint);
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers as Record<string, string> | undefined),
    };
    const token = this.getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, { ...options, headers, credentials: 'include' });
    const requestId = response.headers.get('X-Request-ID');

    if (response.status === 204) {
      return { success: true } as CoreApiResponse<T>;
    }

    const text = await response.text();

    if (!response.ok) {
      if (response.status === 401) {
        const isAuthEndpoint =
          endpoint.startsWith('/auth') ||
          endpoint.includes('/login') ||
          endpoint.includes('/refresh-token') ||
          endpoint.includes('/logout');
        const hasRefreshToken = this.config.refreshTokenStorageKey
          ? !!localStorage.getItem(this.config.refreshTokenStorageKey)
          : false;
        const bodyRetryable =
          !options.body ||
          typeof options.body === 'string' ||
          (typeof FormData !== 'undefined' && options.body instanceof FormData);

        if (
          !_retryAfterRefresh &&
          !isAuthEndpoint &&
          hasRefreshToken &&
          bodyRetryable &&
          this.config.refreshHandler
        ) {
          try {
            const newToken = await this.config.refreshHandler();
            if (newToken) {
              this.setToken(newToken);
              return this.request<T>(endpoint, options, true);
            }
          } catch {
            // fall through → unauthorized
          }
        }

        this.clearToken();
        if (this.config.onUnauthorized) {
          this.config.onUnauthorized();
        } else {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
      }

      if (response.status >= 502 && response.status <= 504) {
        const err: CoreApiError = new Error(
          'Servizio temporaneamente non disponibile. Riprova tra qualche secondo.'
        );
        err.status = response.status;
        if (requestId) err.requestId = requestId;
        throw err;
      }

      let errorData: unknown = null;
      const contentType = response.headers.get('content-type') || '';
      if (text && contentType.includes('application/json')) {
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = null;
        }
      }
      const parsed = errorData as { error?: { message?: string } | string; message?: string } | null;
      const rawError = parsed?.error;
      const message =
        (typeof rawError === 'object' && rawError !== null ? rawError.message : rawError) ||
        parsed?.message ||
        `HTTP ${response.status}`;
      const err: CoreApiError = new Error(message);
      err.status = response.status;
      err.data = errorData;
      if (requestId) err.requestId = requestId;
      throw err;
    }

    if (!text) {
      return { success: true } as CoreApiResponse<T>;
    }
    try {
      return JSON.parse(text) as CoreApiResponse<T>;
    } catch {
      const err: CoreApiError = new Error('Risposta del server non valida (JSON malformato)');
      err.status = response.status;
      throw err;
    }
  }
}
